import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Services/Firebase/FirebaseIndex';
import { useAuth } from '../Auth/AuthContext';
import debug from '../../utils/debug';

// Create the context
const UserProfileContext = createContext();

// Storage keys for caching
const PROFILE_STORAGE_KEY = 'gigify_user_profile';
const PROFILE_TIMESTAMP_KEY = 'gigify_profile_timestamp';
const PROFILE_TYPE_KEY = 'gigify_profile_type';
const PROFILE_TTL = 60 * 60 * 1000; // 60 minutes - extended for stability

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};

// Helper to retrieve cached profile data
const getCachedProfile = (userType, userId) => {
  try {
    const profileJson = sessionStorage.getItem(PROFILE_STORAGE_KEY);
    const timestampStr = sessionStorage.getItem(PROFILE_TIMESTAMP_KEY);
    const cachedType = sessionStorage.getItem(PROFILE_TYPE_KEY);
    
    if (!profileJson || !timestampStr || !cachedType) {
      return { profile: null, isValid: false };
    }
    
    const timestamp = parseInt(timestampStr, 10);
    const now = Date.now();
    
    // Check if profile exists, is for the correct user type, and is not expired
    if (profileJson && timestamp && now < timestamp + PROFILE_TTL && cachedType === userType) {
      const cachedProfile = JSON.parse(profileJson);
      
      // Verify this profile belongs to the current user
      if (cachedProfile && cachedProfile.userUID === userId) {
        debug.log('UserProfileContext', 'Using cached profile', { 
          userType, 
          expiresIn: Math.round((timestamp + PROFILE_TTL - now) / 1000) + ' seconds' 
        });
        return { profile: cachedProfile, isValid: true };
      }
    }
    
    // Cache is invalid or expired
    return { profile: null, isValid: false };
  } catch (error) {
    debug.error('UserProfileContext', 'Error retrieving cached profile', error);
    return { profile: null, isValid: false };
  }
};

// Helper to store profile data in session storage
const cacheProfile = (profile, userType) => {
  try {
    if (!profile) {
      // Clear the cache if no profile
      sessionStorage.removeItem(PROFILE_STORAGE_KEY);
      sessionStorage.removeItem(PROFILE_TIMESTAMP_KEY);
      sessionStorage.removeItem(PROFILE_TYPE_KEY);
      debug.log('UserProfileContext', 'Cleared profile cache');
      return;
    }
    
    // Store the profile, timestamp, and user type
    sessionStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
    sessionStorage.setItem(PROFILE_TIMESTAMP_KEY, Date.now().toString());
    sessionStorage.setItem(PROFILE_TYPE_KEY, userType || '');
    
    debug.log('UserProfileContext', 'Cached user profile', { 
      userType, 
      expiresIn: Math.round(PROFILE_TTL / 1000) + ' seconds' 
    });
  } catch (error) {
    debug.error('UserProfileContext', 'Error caching profile', error);
  }
};

export const UserProfileProvider = ({ children }) => {
  const { currentUser, userType, authReady, isAdmin } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false); // New state for background refreshes
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Main function to fetch user profile from Firestore
  const fetchUserProfile = async (userId, type, isBackgroundRefresh = false) => {
    if (!userId || !type) {
      debug.warn('UserProfileContext', 'Cannot fetch profile - missing userId or type', {
        userId,
        type,
        authUID: currentUser?.uid,
        userIDsMatch: currentUser?.uid === userId
      });
      setProfile(null);
      setLoading(false);
      return null;
    }

    debug.firebase('UserProfileContext', `Fetching ${type} profile`, {
      userId,
      authUID: currentUser?.uid,
      userIDsMatch: currentUser?.uid === userId,
      profileType: type,
      isBackgroundRefresh
    });

    // If this is a background refresh and we have existing profile data, don't set loading
    if (isBackgroundRefresh && profile) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Check cache first (skip cache check on background refresh to get fresh data)
      if (!isBackgroundRefresh) {
        const { profile: cachedProfile, isValid } = getCachedProfile(type, userId);
        if (isValid && cachedProfile) {
          setProfile(cachedProfile);
          setLastUpdated(Date.now());
          setLoading(false);
          setIsRefreshing(false);
          return cachedProfile;
        }
      }
      
      // Basic user profile
      let profileData = {};
      
      // Special handling for admin user type
      if (type === "Admin") {
        // Fetch admin user data from AdminUsers collection
        debug.firebase('UserProfileContext', 'Fetching admin profile', {
          firestorePath: `doc(db, "AdminUsers", "${userId}")`,
          userId,
          authUID: currentUser?.uid
        });
        
        const adminDocRef = doc(db, "AdminUsers", userId);
        const adminSnapshot = await getDoc(adminDocRef);
        
        debug.firebase('UserProfileContext', 'Admin document result', {
          exists: adminSnapshot.exists(),
          userId,
          hasRoleId: adminSnapshot.exists() ? !!adminSnapshot.data()?.roleId : false
        });
        
        if (adminSnapshot.exists()) {
          profileData = adminSnapshot.data();
          // Check for associated role if exists
          if (profileData.roleId) {
            const roleRef = doc(db, "AdminRoles", profileData.roleId);
            const roleSnapshot = await getDoc(roleRef);
            
            if (roleSnapshot.exists()) {
              profileData.role = roleSnapshot.data();
            }
          }
        }
        
        // Add timestamps and user info
        profileData.userUID = userId;
        profileData.userType = type;
        profileData._lastFetched = Date.now();
        
        // Update state
        setProfile(profileData);
        setLastUpdated(Date.now());
        
        // Cache the profile
        cacheProfile(profileData, type);

        debug.firebase('UserProfileContext', 'Successfully fetched Admin profile', { userId });
        setLoading(false);
        setIsRefreshing(false);
        return profileData;
      }
      
      // Common collections for both regular user types
      debug.firebase('UserProfileContext', 'Fetching base user profile', {
        firestorePath: `doc(db, "Users", "${userId}")`,
        userId,
        authUID: currentUser?.uid,
        profileType: type
      });
      
      const userDocRef = doc(db, "Users", userId);
      const userSnapshot = await getDoc(userDocRef);
      
      debug.firebase('UserProfileContext', 'Base user document result', {
        exists: userSnapshot.exists(),
        userId,
        profileType: type
      });
      
      if (userSnapshot.exists()) {
        profileData = userSnapshot.data();
      }
      
      // Fetch compliance data for both user types
      const complianceRef = doc(db, "ComplianceVault", userId);
      const complianceSnapshot = await getDoc(complianceRef);
      
      if (complianceSnapshot.exists()) {
        profileData.compliance = complianceSnapshot.data();
      }
      
      // Type-specific collections
      if (type === "Talent") {
        // Talent-specific collections
        const blueprintRef = doc(db, "TalentBlueprint", userId);
        const studioRef = doc(db, "TalentStudio", userId);
        const performanceHubRef = doc(db, "PerformanceHub", userId);
        const pricingPortfolioRef = doc(db, "PricingPortfolio", userId);
        
        const [blueprintSnapshot, studioSnapshot, performanceHubSnapshot, pricingPortfolioSnapshot] = await Promise.all([
          getDoc(blueprintRef),
          getDoc(studioRef),
          getDoc(performanceHubRef),
          getDoc(pricingPortfolioRef)
        ]);
        
        if (blueprintSnapshot.exists()) {
          profileData.blueprint = blueprintSnapshot.data();
        }
        
        if (studioSnapshot.exists()) {
          profileData.studio = studioSnapshot.data();
          debug.firebase('UserProfileContext', '🎨 TalentStudio data loaded', {
            hasMedia: !!(profileData.studio.media),
            hasPrimaryPhoto: !!(profileData.studio.media?.primaryPhoto),
            primaryPhotoValue: profileData.studio.media?.primaryPhoto,
            primaryPhotoType: typeof profileData.studio.media?.primaryPhoto,
            hasPhotos: !!(profileData.studio.media?.photos),
            photoCount: profileData.studio.media?.photos?.length || 0,
            firstPhoto: profileData.studio.media?.photos?.[0] || null,
            mediaKeys: profileData.studio.media ? Object.keys(profileData.studio.media) : []
          });
        } else {
          debug.warn('UserProfileContext', 'No TalentStudio document found for user', { userId });
        }
        
        if (performanceHubSnapshot.exists()) {
          profileData.performanceHub = performanceHubSnapshot.data();
        }
        
        if (pricingPortfolioSnapshot.exists()) {
          profileData.pricingPortfolio = pricingPortfolioSnapshot.data();
        }
      } else if (type === "Organiser") {
        // Organiser-specific collections
        const blueprintRef = doc(db, "OrganiserBlueprint", userId);
        const blueprintSnapshot = await getDoc(blueprintRef);
        
        if (blueprintSnapshot.exists()) {
          profileData.blueprint = blueprintSnapshot.data();
        }
      }
      
      // Add timestamps and user info
      profileData.userUID = userId;
      profileData.userType = type;
      profileData._lastFetched = Date.now();
      
      // Update state
      setProfile(profileData);
      setLastUpdated(Date.now());
      
      // Cache the profile
      cacheProfile(profileData, type);

      debug.firebase('UserProfileContext', `Successfully fetched ${type} profile`, { userId });
      setLoading(false);
      setIsRefreshing(false);
      return profileData;
    } catch (error) {
      debug.error('UserProfileContext', 'Error fetching user profile', { 
        userId, 
        type, 
        error: error.message,
        errorCode: error.code,
        errorDetails: error.details,
        authUID: currentUser?.uid,
        userIDsMatch: currentUser?.uid === userId,
        firestoreCollections: {
          Users: `Users/${userId}`,
          TalentBlueprint: type === 'Talent' ? `TalentBlueprint/${userId}` : 'N/A',
          OrganiserBlueprint: type === 'Organiser' ? `OrganiserBlueprint/${userId}` : 'N/A',
          AdminUsers: type === 'Admin' ? `AdminUsers/${userId}` : 'N/A'
        }
      });
      setError(error);
      setLoading(false);
      setIsRefreshing(false);
      return null;
    }
  };
  
  // Check if profile should be refreshed
  const shouldRefreshProfile = () => {
    if (!profile || !lastUpdated) return true;
    
    const timeSinceUpdate = Date.now() - lastUpdated;
    return timeSinceUpdate > PROFILE_TTL;
  };

  // Refresh user profile data
  const refreshProfile = async (force = false, isBackgroundRefresh = false) => {
    if (!currentUser || !userType) return null;

    // Only refresh if needed, unless forced
    if (!force && !shouldRefreshProfile()) {
      debug.log('UserProfileContext', 'Skipping refresh - profile is recent');
      return profile;
    }

    return await fetchUserProfile(currentUser.uid, userType, isBackgroundRefresh);
  };

  // Initial fetch effect
  useEffect(() => {
    debug.component.mount('UserProfileContext');
    
    const initializeProfile = async () => {
      if (!authReady) return;
      
      // If we have a user and user type, try to fetch the profile
      if (currentUser && userType) {
        await fetchUserProfile(currentUser.uid, userType);
      } else {
        // Clear profile state if no user
        setProfile(null);
        setLoading(false);
        cacheProfile(null, null); // Clear any cached profile
      }
    };
    
    initializeProfile();
    
    return () => {
      debug.component.unmount('UserProfileContext');
    };
  }, [currentUser, userType, authReady]);

  // Clear profile on user change or logout
  useEffect(() => {
    if (!currentUser) {
      setProfile(null);
      cacheProfile(null, null);
    }
  }, [currentUser]);

  // Auto-refresh removed for stability - profile only refreshes on:
  // 1. Initial login
  // 2. Explicit user action (manual refresh)
  // 3. User navigating to profile/settings pages

  const value = useMemo(() => ({
    profile,
    loading,
    isRefreshing,
    error,
    lastUpdated,
    refreshProfile,
    talentBlueprint: profile?.blueprint || null,
    talentStudio: profile?.studio || null,
    performanceHub: profile?.performanceHub || null,
    pricingPortfolio: profile?.pricingPortfolio || null,
    complianceVault: profile?.compliance || null,
    organiserBlueprint: profile?.blueprint || null,
    get adminRole() { return profile?.role || null; },
    get adminPermissions() { return profile?.permissions || [] },
    get isSuperAdmin() { return profile?.isSuperAdmin || false },
    get profileDisplayName() {
      if (!profile) return null;
      if (userType === 'Admin') {
        return profile.firstName && profile.lastName
          ? `${profile.firstName} ${profile.lastName}`
          : profile.email?.split('@')[0] || 'Admin';
      } else if (userType === 'Talent') {
        return profile.blueprint?.personalInfo?.stageName ||
               profile.blueprint?.stageName ||
               `${profile.profile?.firstName || ''} ${profile.profile?.lastName || ''}`.trim();
      } else {
        return profile.blueprint?.businessName ||
               `${profile.profile?.firstName || ''} ${profile.profile?.lastName || ''}`.trim();
      }
    },
    get profilePhotoURL() {
      if (!profile) return null;

      // Admin users use admin icon (handled in component)
      if (userType === 'Admin') {
        return null;
      }

      // Talent profile photo resolution
      if (userType === 'Talent') {
        // Priority 1: TalentBlueprint photoURL (primary source - used during registration)
        if (profile.blueprint?.photoURL && typeof profile.blueprint.photoURL === 'string') {
          return profile.blueprint.photoURL;
        }

        // Priority 2: TalentStudio media.primaryPhoto (fallback - used in studio)
        if (profile.studio?.media?.primaryPhoto) {
          let photoUrl = profile.studio.media.primaryPhoto;

          // Handle legacy nested format
          if (typeof photoUrl !== 'string' && photoUrl?.original) {
            photoUrl = photoUrl.original;
          }

          if (photoUrl && typeof photoUrl === 'string') {
            return photoUrl;
          }
        }

        return null;
      }

      // Organiser profile photo resolution
      if (userType === 'Organiser') {
        const photoUrl = profile.blueprint?.profilePhoto;
        return (photoUrl && typeof photoUrl === 'string') ? photoUrl : null;
      }

      return null;
    },
    get socialLinks() { return profile?.studio?.socialMedia || null; },
    get mediaContent() { return profile?.studio?.media || null; },
    get isProfileComplete() {
      if (!profile) return false;
      if (userType === 'Admin') return true; // Admin profiles are always considered complete
      if (userType === 'Talent') {
        return !!(profile.blueprint && profile.studio);
      }
      return !!profile.blueprint;
    }
  }), [profile, loading, isRefreshing, error, lastUpdated, userType]); // Dependencies that value relies on

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

export default UserProfileContext;